import { useEffect, useId, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';

export interface ListViewItem {
  id: string;
  primaryText: string;
  secondaryText?: string;
  /** Optional icon rendered to the left of the text. */
  icon?: ReactNode;
  /** When true the item is shown but cannot be clicked. */
  disabled?: boolean;
}

export interface ListViewChiclet {
  id: string;
  text: string;
  icon?: ReactNode;
}

export interface ListViewFilters<TValue = unknown> {
  /** Current filter state; owned by the caller. */
  filterValue: TValue;
  onFilterChange: (value: TValue) => void;
  /** Caller-provided filter UI, rendered in the collapsible area below the search box. */
  render: (ctx: { filterValue: TValue; onFilterChange: (value: TValue) => void }) => ReactNode;
  /** Shows an indicator on the filter icon when filters are applied. The filter area starts open if true on first render. */
  isActive?: boolean;
  /** Accessible label for the filter toggle. */
  label?: string;
}

export interface ListViewPanelProps<TFilter = unknown> {
  items: ListViewItem[];
  /** Set when `items` are recent items; shows a subtle "Recent" header. The caller decides when this applies. */
  isRecent?: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onItemClick: (item: ListViewItem) => void;
  /** Called when the end of the list becomes visible and more items can be loaded. */
  onLoadMore: () => void;
  hasMore: boolean;
  isLoading?: boolean;
  selectedItemId?: string;
  searchPlaceholder?: string;
  /** Replaces the search box, e.g. with an autocomplete; `searchValue`, `onSearchChange` and `chiclets` are then unused. */
  searchSlot?: ReactNode;
  emptyMessage?: string;
  /** When provided, shows a filter toggle next to the search box. */
  filters?: ListViewFilters<TFilter>;
  /** Tokens rendered inside the search input, left of the text. Backspace on empty input removes the last one. */
  chiclets?: ListViewChiclet[];
  onChicletRemove?: (id: string) => void;
  children?: ReactNode;
}

export function ListViewPanel<TFilter = unknown>({
  items,
  isRecent = false,
  searchValue,
  onSearchChange,
  onItemClick,
  onLoadMore,
  hasMore,
  isLoading = false,
  selectedItemId,
  searchPlaceholder = 'Search...',
  searchSlot,
  emptyMessage = 'No results found.',
  filters,
  chiclets = [],
  onChicletRemove,
  children,
}: ListViewPanelProps<TFilter>): ReactElement {
  const [filtersOpen, setFiltersOpen] = useState(Boolean(filters?.isActive));
  // Overflow is clipped only during the expand/collapse animation so popups (e.g. autocomplete) can escape.
  const [filtersExpanded, setFiltersExpanded] = useState(Boolean(filters?.isActive));
  const filtersId = useId();
  const scrollRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const sentinelRef = useRef<HTMLLIElement>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  const canLoadMore = hasMore && !isLoading;

  const prevChicletCount = useRef(chiclets.length);
  useEffect(() => {
    if (chiclets.length > prevChicletCount.current) searchRef.current?.focus();
    prevChicletCount.current = chiclets.length;
  }, [chiclets.length]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!canLoadMore || !sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMoreRef.current();
      },
      { root: scrollRef.current, rootMargin: '100px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [canLoadMore, items.length]);

  return (
    <div className="flex h-full min-h-0 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex w-1/3 min-w-0 flex-col border-r border-slate-200">
        <div className="border-b border-slate-200 p-3">
          <div className="flex items-center gap-2">
            {searchSlot ?? (
              <div
                onClick={() => searchRef.current?.focus()}
                className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 py-1.5 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20"
              >
                {chiclets.length === 0 && (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="pointer-events-none ml-1 h-4 w-4 shrink-0 text-slate-400"
                    aria-hidden="true"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                  </svg>
                )}
                {chiclets.map((c) => (
                  <span
                    key={c.id}
                    title={c.text}
                    className="flex max-w-[150px] items-center gap-1.5 rounded-full border border-slate-200 bg-slate-100 py-0.5 pl-2 pr-1 text-xs text-slate-700"
                  >
                    {c.icon && (
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center text-slate-500 [&>svg]:h-4 [&>svg]:w-4">
                        {c.icon}
                      </span>
                    )}
                    <span className="truncate">{c.text}</span>
                    <button
                      type="button"
                      onClick={() => onChicletRemove?.(c.id)}
                      aria-label={`Remove ${c.text}`}
                      className="flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        className="h-3 w-3"
                        aria-hidden="true"
                      >
                        <path d="M18 6 6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
                <input
                  ref={searchRef}
                  type="search"
                  value={searchValue}
                  onChange={(e) => onSearchChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Backspace' && searchValue === '' && chiclets.length > 0) {
                      e.preventDefault();
                      onChicletRemove?.(chiclets[chiclets.length - 1].id);
                      return;
                    }
                    if (e.key !== 'ArrowDown') return;
                    const firstResult =
                      scrollRef.current?.querySelector<HTMLButtonElement>('[data-list-item]');
                    if (!firstResult) return;
                    e.preventDefault();
                    firstResult.focus();
                  }}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  className="min-w-[6rem] flex-1 bg-transparent py-0.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none [&::-webkit-search-cancel-button]:appearance-none"
                />
              </div>
            )}
            {filters && (
              <button
                type="button"
                onClick={() => {
                  setFiltersExpanded(false);
                  setFiltersOpen((o) => !o);
                }}
                aria-expanded={filtersOpen}
                aria-controls={filtersId}
                aria-label={filters.label ?? 'Filters'}
                className={`relative flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg border transition-colors ${
                  filtersOpen
                    ? 'border-blue-500 bg-blue-50 text-blue-600'
                    : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
                }`}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-4 w-4"
                  aria-hidden="true"
                >
                  <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" />
                </svg>
                {filters.isActive && (
                  <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-blue-600" />
                )}
              </button>
            )}
          </div>
          {filters && (
            <div
              id={filtersId}
              onTransitionEnd={(e) => {
                if (e.target === e.currentTarget && filtersOpen) setFiltersExpanded(true);
              }}
              className={`grid transition-[grid-template-rows,opacity] duration-200 ease-in-out ${
                filtersOpen ? 'grid-rows-[1fr] opacity-100' : 'invisible grid-rows-[0fr] opacity-0'
              }`}
            >
              <div className={`min-h-0 ${filtersExpanded ? '' : 'overflow-hidden'}`}>
                <div className="pt-3">
                  {filters.render({
                    filterValue: filters.filterValue,
                    onFilterChange: filters.onFilterChange,
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
          {items.length === 0 && !isLoading ? (
            <p className="px-4 py-10 text-center text-sm text-slate-500">{emptyMessage}</p>
          ) : (
            <ul
              className="divide-y divide-slate-100"
              onKeyDown={(e) => {
                if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
                const buttons = Array.from(
                  e.currentTarget.querySelectorAll<HTMLButtonElement>('[data-list-item]')
                );
                if (!(e.target instanceof HTMLButtonElement)) return;
                const current = buttons.indexOf(e.target);
                if (current < 0) return;
                e.preventDefault();
                if (e.key === 'ArrowUp' && current === 0) {
                  searchRef.current?.focus();
                } else {
                  buttons[current + (e.key === 'ArrowDown' ? 1 : -1)]?.focus();
                }
              }}
            >
              {isRecent && (
                <li className="px-4 pb-1 pt-3 text-xs font-medium uppercase tracking-wide text-slate-400">
                  Recent
                </li>
              )}
              {items.map((item) => {
                const selected = item.id === selectedItemId;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      data-list-item
                      onClick={() => {
                        if (!item.disabled) onItemClick(item);
                      }}
                      aria-current={selected ? 'true' : undefined}
                      aria-disabled={item.disabled ? 'true' : undefined}
                      className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${
                        item.disabled ? 'cursor-default' : 'cursor-pointer'
                      } ${
                        selected
                          ? 'bg-blue-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500'
                          : `${item.disabled ? '' : 'hover:bg-slate-50 '}focus:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500`
                      }`}
                    >
                      {item.icon && (
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center text-slate-500">
                          {item.icon}
                        </span>
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-slate-900">
                          {item.primaryText}
                        </span>
                        {item.secondaryText && (
                          <span className="mt-0.5 block truncate text-xs text-slate-500">
                            {item.secondaryText}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
              {hasMore && <li ref={sentinelRef} aria-hidden="true" className="h-px" />}
              {isLoading && (
                <li className="flex justify-center py-4">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600" />
                </li>
              )}
            </ul>
          )}
        </div>
      </div>

      <div className="min-w-0 w-2/3 overflow-y-auto p-6">{children}</div>
    </div>
  );
}
